import "../AuthStyle.css";
import { Link, useLocation } from "react-router-dom";
import { useCallback, useContext, useState, useEffect, useRef } from "react";
import { AppContext } from "../../../context/AppContextProvider";
import {
  userRegister,
  userRegisterValidate,
  sendOTPSMSAgain,
  checkRegisterSubdomain,
} from "../../../network/network";
import { Form, Input, Button, Col, Row, Radio, Spin, Dropdown } from "antd";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Pricing from "../../../components/Pricing/Pricing";
import { DownOutlined } from "@ant-design/icons";
import { handleApiError } from "../../../utils/errorHandler";
import { showSuccessMessage } from "../../../utils/antdMessage";
import { arabicNameRules, cleanArabicInput } from "../../../utils/validators";
import { createDailyPlanSelection } from "../../../utils/subscriptionPlans";
import { UserType } from "../../../configs/constants";
import { isTenantHost } from "../../../network/tenant";

const OTP_RESEND_TIME = 120; // 2 minutes

const normalizeSubdomainInput = (value) =>
  String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+/g, "");

const slugify = (value) => normalizeSubdomainInput(value).replace(/-+$/g, "");

const shouldHandleSubdomainError = (error) => {
  if (error?.response?.status !== 422) return false;

  const responseData = error?.response?.data || {};
  const nestedErrors = responseData?.data?.errors;
  const legacyErrors = responseData?.errors;

  return Boolean(
    nestedErrors?.subdomain ||
    legacyErrors?.subdomain ||
    (nestedErrors == null && legacyErrors == null),
  );
};

const RegisterForm = () => {
  const { setAuthUser, setFullPageLoading, appConfig, setAppConfigWrapper } =
    useContext(AppContext);

  const [pricingVisible, setPricingVisible] = useState(true);
  const [OTPFormVisible, setOTPFormVisible] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const timerRef = useRef(null);
  const subdomainTimerRef = useRef(null);
  const subdomainRequestRef = useRef(0);
  const subdomainEditedRef = useRef(false);
  const [loading, setloading] = useState(false);
  const [planSelection, setPlanSelection] = useState({});
  const [resellerNumber, setResellerNumber] = useState("");
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [subdomain, setSubdomain] = useState("");
  const [subdomainState, setSubdomainState] = useState({
    status: "idle",
    message: "",
    suggestions: [],
  });
  const [registerForm] = Form.useForm();
  const [otpForm] = Form.useForm();

  const { t } = useTranslation();
  const { search } = useLocation();
  const businessName = Form.useWatch("business_name", registerForm) || "";
  const tenantBaseDomain = (
    process.env.REACT_APP_TENANT_BASE_DOMAIN || "anypos.ai"
  )
    .trim()
    .replace(/^\.+|\.+$/g, "");

  const checkSubdomain = useCallback(
    async (slugValue, nameValue, serverMessage = "") => {
      const normalizedSlug = slugify(slugValue);

      if (!normalizedSlug) {
        setSubdomainState({
          status: "invalid",
          message: serverMessage || t("store_url_invalid"),
          suggestions: [],
        });
        return;
      }

      const requestId = subdomainRequestRef.current + 1;
      subdomainRequestRef.current = requestId;
      setSubdomainState((current) => ({
        ...current,
        status: "checking",
        message: serverMessage || t("checking_store_url"),
      }));

      try {
        const response = await checkRegisterSubdomain({
          slug: normalizedSlug,
          name: nameValue || "",
        });

        if (requestId !== subdomainRequestRef.current) return;

        const data = response?.data?.data || {};
        setSubdomainState({
          status: data.available ? "available" : "taken",
          message:
            serverMessage ||
            (data.available ? t("store_url_available") : t("store_url_taken")),
          suggestions: data.suggestions || [],
        });
      } catch {
        if (requestId !== subdomainRequestRef.current) return;

        setSubdomainState((current) => ({
          ...current,
          status: serverMessage ? "invalid" : "idle",
          message: serverMessage,
        }));
      }
    },
    [t],
  );

  const handleSubdomainError = useCallback(
    (error) => {
      const responseData = error?.response?.data || {};
      const subdomainErrors =
        responseData?.data?.errors?.subdomain ||
        responseData?.errors?.subdomain;
      const fieldMessage = Array.isArray(subdomainErrors)
        ? subdomainErrors[0]
        : typeof subdomainErrors === "string"
          ? subdomainErrors
          : "";
      const serverMessage =
        fieldMessage ||
        responseData?.error ||
        responseData?.message ||
        t("store_url_invalid");
      const suggestions =
        responseData?.data?.suggestions ||
        responseData?.data?.data?.suggestions ||
        responseData?.suggestions ||
        [];

      setSubdomainState({
        status: "invalid",
        message: serverMessage,
        suggestions,
      });
      checkSubdomain(subdomain, businessName, serverMessage);
    },
    [businessName, checkSubdomain, subdomain, t],
  );

  useEffect(() => {
    if (!subdomain) return;

    subdomainTimerRef.current = setTimeout(() => {
      checkSubdomain(subdomain, businessName);
    }, 600);

    return () => clearTimeout(subdomainTimerRef.current);
  }, [businessName, checkSubdomain, subdomain]);

  useEffect(() => {
    const query = new URLSearchParams(search);
    const reseller = query.get("reseller_number") || "";
    setResellerNumber(reseller);

    if (reseller) {
      registerForm.setFieldsValue({
        reseller_number: reseller,
      });
    }
  }, [registerForm, search]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (subdomainTimerRef.current) {
        clearTimeout(subdomainTimerRef.current);
      }
    };
  }, []);

  const onOTPFormFinish = async (otpValues) => {
    try {
      setloading(true);

      const values = await registerForm.validateFields();

      values.selected_addons = JSON.stringify(
        selectedAddons.map((item) => ({
          id: item.id,
          name: item.name,
        })),
      );

      const response = await userRegister({
        ...otpValues,
        ...values,
        ...planSelection,
      });

      if (response.data && response.data.success) {
        setFullPageLoading(true);
        localStorage.setItem(
          "access_token",
          response.data.data.user.access_token,
        );
        setAuthUser(response.data.data.user);
        setFullPageLoading(false);
        return;
      }

      if (response?.data?.success === false) {
        handleApiError(response, t);
      }
    } catch (error) {
      if (shouldHandleSubdomainError(error)) {
        setOTPFormVisible(false);
        handleSubdomainError(error);
      }
      handleApiError(error, t);
    } finally {
      setloading(false);
    }
  };

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startTimer = () => {
    clearTimer();

    setRemainingTime(OTP_RESEND_TIME);

    timerRef.current = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime <= 1) {
          clearTimer();
          return 0;
        }

        return prevTime - 1;
      });
    }, 1000);
  };

  const handleSendOTPSMSAgain = async () => {
    try {
      const mobileNumber = registerForm.getFieldValue("phone");

      const response = await sendOTPSMSAgain({ number: mobileNumber });

      if (response?.data?.success === false) {
        handleApiError(response, t);
        return;
      }

      startTimer();
      setOTPFormVisible(true);

      showSuccessMessage(t("otp_sent_successfully"));
    } catch (error) {
      handleApiError(error, t);
    }
  };

  const handleRegisterValidation = async () => {
    try {
      setloading(true);

      const normalizedSubdomain = slugify(
        registerForm.getFieldValue("subdomain"),
      );
      registerForm.setFieldsValue({ subdomain: normalizedSubdomain });
      setSubdomain(normalizedSubdomain);

      const values = await registerForm.validateFields();
      const response = await userRegisterValidate(values);

      if (response.data && response.data.success) {
        startTimer();
        setOTPFormVisible(true);
        return;
      }

      if (response?.data?.success === false) {
        handleApiError(response, t);
      }
    } catch (error) {
      if (shouldHandleSubdomainError(error)) {
        handleSubdomainError(error);
      }
      handleApiError(error, t);
    } finally {
      setloading(false);
    }
  };

  const handleArabicBusinessNameChange = (event) => {
    registerForm.setFieldsValue({
      business_arabic_name: cleanArabicInput(event.target.value),
    });
  };

  const handleBusinessNameChange = (event) => {
    if (subdomainEditedRef.current) return;

    const nextSubdomain = slugify(event.target.value);
    registerForm.setFieldsValue({ subdomain: nextSubdomain });
    setSubdomain(nextSubdomain);
  };

  const handleSubdomainChange = (event) => {
    const nextSubdomain = normalizeSubdomainInput(event.target.value);
    subdomainEditedRef.current = true;
    registerForm.setFieldsValue({ subdomain: nextSubdomain });
    setSubdomain(nextSubdomain);

    if (!nextSubdomain) {
      setSubdomainState({
        status: "invalid",
        message: t("store_url_invalid"),
        suggestions: [],
      });
    }
  };

  const handleSubdomainBlur = () => {
    if (subdomainTimerRef.current) {
      clearTimeout(subdomainTimerRef.current);
    }

    const normalizedSubdomain = slugify(subdomain);
    registerForm.setFieldsValue({ subdomain: normalizedSubdomain });
    setSubdomain(normalizedSubdomain);
    checkSubdomain(normalizedSubdomain, businessName);
  };

  const selectSubdomainSuggestion = (suggestion) => {
    const nextSubdomain = slugify(suggestion?.slug || suggestion);
    subdomainEditedRef.current = true;
    registerForm.setFieldsValue({ subdomain: nextSubdomain });
    setSubdomain(nextSubdomain);
  };

  const callbackSelection = (plan, users_count, selectedAddons) => {
    const selection = createDailyPlanSelection(plan, users_count);
    if (!selection) return;

    setPricingVisible(false);
    setSelectedAddons(selectedAddons);
    setPlanSelection(selection);

    registerForm.setFieldsValue({
      plan_text: plan.name || t("daily"),
      type: UserType.BusinessOwner,
    });
  };

  const LangOptions = [
    {
      key: "1",
      label: (
        <div
          onClick={() => {
            setAppConfigWrapper({ direction: "ltr" });
          }}
        >
          English
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div
          onClick={() => {
            setAppConfigWrapper({ direction: "rtl" });
          }}
        >
          عربي
        </div>
      ),
    },
  ];

  return (
    <>
      <Spin spinning={loading}>
        <div
          style={{
            background: "var(--color-light-bg)",
            minHeight: "100vh",
            direction: appConfig.direction
              ? appConfig.direction === "ltr"
                ? "ltr"
                : "rtl"
              : "ltr",
          }}
        >
          <header
            className="header"
            style={{
              background: "var(--color-white)",
              borderBottom: "1px solid var(--color-primary)",
              boxShadow: "0 4px 18px var(--color-shadow-soft)",
              position: "sticky",
              top: 0,
              zIndex: 50,
              width: "100%",
            }}
          >
            <div className="main-wrapper">
              <div className="app-flex-between">
                <div className="app-flex">
                  <a
                    href="https://anypos.ai/"
                    target="_blank"
                    rel="noreferrer"
                    className="app-flex"
                  >
                    <img
                      src="/anypos-logo.svg"
                      alt="anypos"
                      className="main_logo"
                    />
                  </a>
                </div>
                <div className="Button-area app-flex-align-center-gap-10">
                  {!resellerNumber && (
                    <a
                      href="https://anypos.ai/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Button type="default" className="md_down_display_none">
                        {t("visit_website")}
                      </Button>
                    </a>
                  )}
                  {!resellerNumber && (
                    <Link to="/login">
                      <Button type="primary">{t("login")}</Button>
                    </Link>
                  )}
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: LangOptions,
                    }}
                    className="md_down_display_none"
                  >
                    <Button type="default">
                      {appConfig.direction
                        ? appConfig.direction === "ltr"
                          ? "English"
                          : "عربي"
                        : "English"}
                      <span
                        style={{
                          margin: appConfig.direction
                            ? appConfig.direction === "ltr"
                              ? "0 0 0 8px"
                              : "0 8px 0 0"
                            : "0 0 0 8px",
                        }}
                      >
                        <DownOutlined className="app-text-xs" />
                      </span>
                    </Button>
                  </Dropdown>

                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: LangOptions,
                    }}
                    className="md_up_display_none"
                  >
                    <img
                      src="/icons/globe.png"
                      alt="language"
                      style={{
                        width: "fit-content",
                        height: "fit-content",
                        backgroundColor: "var(--color-primary)",
                        borderRadius: "5px",
                        padding: "2px",
                      }}
                    />
                  </Dropdown>
                </div>
              </div>
            </div>
          </header>

          <div className="main-wrapper">
            <div className="auth_area">
              {pricingVisible ? (
                <Pricing callbackSelection={callbackSelection} />
              ) : (
                <>
                  <div style={{ display: !OTPFormVisible ? "block" : "none" }}>
                    <Row>
                      <Col xs={24} sm={24} md={24} lg={2} xl={3} xxl={4} />
                      <Col xs={24} sm={24} md={24} lg={20} xl={18} xxl={16}>
                        <div
                          style={{
                            backgroundColor: "var(--color-white)",
                            padding: "30px",
                            boxShadow: "0 4px 20px var(--color-shadow-soft)",
                            borderRadius: "16px",
                            border: "1px solid var(--color-border)",
                          }}
                        >
                          <Form
                            name="register-user-form"
                            layout="vertical"
                            onFinish={handleRegisterValidation}
                            form={registerForm}
                          >
                            <div className="app-auth-form-footer">
                              <h2>{t("sign_up")}</h2>
                              <p
                                style={{
                                  marginTop: "12px",
                                  color: "var(--color-heading)",
                                  fontSize: "17px",
                                }}
                              >
                                {t("complete_signup_details")}
                              </p>
                            </div>

                            <Row
                              gutter={{
                                xs: 0,
                                sm: 20,
                                md: 20,
                                lg: 20,
                                xl: 20,
                                xxl: 20,
                              }}
                            >
                              <Col xs={24} sm={12}>
                                <Form.Item
                                  name="first_name"
                                  label={t("first_name")}
                                  rules={[
                                    {
                                      required: true,
                                      message: t(
                                        "validate_first_name_required",
                                      ),
                                      whitespace: true,
                                    },
                                    {
                                      min: 3,
                                      message: t(
                                        "validate_first_name_min_characters",
                                      ),
                                    },
                                  ]}
                                  hasFeedback
                                >
                                  <Input size="large" />
                                </Form.Item>
                              </Col>

                              <Col xs={24} sm={12}>
                                <Form.Item
                                  name="subdomain"
                                  label={t("store_url")}
                                  validateStatus={
                                    subdomainState.status === "checking"
                                      ? "validating"
                                      : subdomainState.status === "available"
                                        ? "success"
                                        : subdomainState.status === "taken" ||
                                            subdomainState.status === "invalid"
                                          ? "error"
                                          : ""
                                  }
                                  help={
                                    <div>
                                      {subdomainState.message ? (
                                        <div>{subdomainState.message}</div>
                                      ) : !subdomain ? (
                                        <div>{t("store_url_required")}</div>
                                      ) : null}
                                      {subdomainState.suggestions.length > 0 ? (
                                        <div>
                                          {t("store_url_suggestions")}:{" "}
                                          {subdomainState.suggestions.map(
                                            (suggestion) => {
                                              const suggestionSlug =
                                                suggestion?.slug || suggestion;

                                              return (
                                                <Button
                                                  key={suggestionSlug}
                                                  type="link"
                                                  size="small"
                                                  onClick={() =>
                                                    selectSubdomainSuggestion(
                                                      suggestion,
                                                    )
                                                  }
                                                  style={{ padding: "0 4px" }}
                                                >
                                                  {suggestionSlug}
                                                </Button>
                                              );
                                            },
                                          )}
                                        </div>
                                      ) : null}
                                      {subdomain ? (
                                        <div>
                                          {t("store_url_preview")}: {subdomain}.
                                          {tenantBaseDomain}
                                        </div>
                                      ) : null}
                                    </div>
                                  }
                                  rules={[
                                    {
                                      required: true,
                                      message: t("store_url_required"),
                                    },
                                    {
                                      pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                                      message: t("store_url_invalid"),
                                    },
                                  ]}
                                  hasFeedback
                                >
                                  <Input
                                    size="large"
                                    addonAfter={`.${tenantBaseDomain}`}
                                    onChange={handleSubdomainChange}
                                    onBlur={handleSubdomainBlur}
                                  />
                                </Form.Item>
                              </Col>

                              <Col xs={24} sm={12}>
                                <Form.Item
                                  name="last_name"
                                  label={t("last_name")}
                                  rules={[
                                    {
                                      required: true,
                                      message: t("validate_last_name_required"),
                                      whitespace: true,
                                    },
                                    {
                                      min: 3,
                                      message: t(
                                        "validate_last_name_min_characters",
                                      ),
                                    },
                                  ]}
                                  hasFeedback
                                >
                                  <Input size="large" />
                                </Form.Item>
                              </Col>

                              <Col xs={24} sm={12}>
                                <Form.Item
                                  name="email"
                                  label={t("email")}
                                  rules={[
                                    {
                                      type: "email",
                                      message: t("validate_email_invalid"),
                                    },
                                  ]}
                                  hasFeedback
                                >
                                  <Input size="large" />
                                </Form.Item>
                              </Col>

                              <Col xs={24} sm={12}>
                                <Form.Item
                                  name="phone"
                                  label={
                                    t("phone_number") + " eg: +966 5xxxxxxx"
                                  }
                                  rules={[
                                    {
                                      required: true,
                                      message: t("validate_phone_required"),
                                    },
                                    {
                                      min: 12,
                                      message: t("validate_phone_exact_limit"),
                                    },
                                    {
                                      max: 12,
                                      message: t("validate_phone_exact_limit"),
                                    },
                                  ]}
                                >
                                  <PhoneInput
                                    localization={["sa"]}
                                    masks={{ sa: "(...) ..-..-.." }}
                                    placeholder="+966 (123) 45-67-89"
                                    country={"sa"}
                                    onlyCountries={["sa"]}
                                    style={{ direction: "ltr" }}
                                  />
                                </Form.Item>
                              </Col>

                              <Col xs={24} sm={12}>
                                <Form.Item
                                  name="password"
                                  label={t("password")}
                                  rules={[
                                    {
                                      required: true,
                                      message: t("validate_password_required"),
                                    },
                                    {
                                      min: 6,
                                      message: t(
                                        "validate_password_min_characters",
                                      ),
                                    },
                                  ]}
                                  hasFeedback
                                >
                                  <Input.Password size="large" />
                                </Form.Item>
                              </Col>

                              <Col xs={24} sm={12}>
                                <Form.Item
                                  name="password_confirmation"
                                  label={t("confirm_password")}
                                  dependencies={["password"]}
                                  hasFeedback
                                  rules={[
                                    {
                                      required: true,
                                      message: t(
                                        "validate_password_confirm_not_matched",
                                      ),
                                    },
                                    ({ getFieldValue }) => ({
                                      validator(_, value) {
                                        if (
                                          !value ||
                                          getFieldValue("password") === value
                                        ) {
                                          return Promise.resolve();
                                        }

                                        return Promise.reject(
                                          new Error(
                                            t(
                                              "validate_password_confirm_not_matched",
                                            ),
                                          ),
                                        );
                                      },
                                    }),
                                  ]}
                                >
                                  <Input.Password size="large" />
                                </Form.Item>
                              </Col>

                              <Col xs={24} sm={12}>
                                <Form.Item
                                  name="business_name"
                                  label={t("business_name")}
                                  rules={[
                                    {
                                      required: true,
                                      message: t(
                                        "validate_business_name_required",
                                      ),
                                      whitespace: true,
                                    },
                                  ]}
                                  hasFeedback
                                >
                                  <Input
                                    size="large"
                                    onChange={handleBusinessNameChange}
                                  />
                                </Form.Item>
                              </Col>

                              <Col xs={24} sm={12}>
                                <Form.Item
                                  name="business_arabic_name"
                                  label={t("business_name_arabic")}
                                  rules={arabicNameRules(t, true)}
                                  hasFeedback
                                >
                                  <Input
                                    size="large"
                                    dir="rtl"
                                    placeholder={t("arabic_only_validation")}
                                    onChange={handleArabicBusinessNameChange}
                                  />
                                </Form.Item>
                              </Col>

                              <Col xs={24} sm={12}>
                                <Form.Item
                                  name="plan_text"
                                  label={t("selected_plan")}
                                >
                                  <Input size="large" disabled />
                                </Form.Item>
                              </Col>

                              <Col xs={24} sm={12}>
                                <Form.Item
                                  name="preferred_contact_time"
                                  label={t("preferred_contact_time")}
                                  rules={[
                                    {
                                      required: true,
                                      message: t(
                                        "validate_preferred_contact_time_required",
                                      ),
                                    },
                                  ]}
                                >
                                  <Radio.Group>
                                    <Radio
                                      value={"09:00 AM - 02:00 PM"}
                                      style={{ direction: "ltr" }}
                                    >
                                      09:00 AM - 02:00 PM
                                    </Radio>
                                    <Radio
                                      value={"02:00 PM - 05:00 PM"}
                                      style={{ direction: "ltr" }}
                                    >
                                      02:00 PM - 05:00 PM
                                    </Radio>
                                  </Radio.Group>
                                </Form.Item>
                              </Col>

                              <Col xs={24} sm={12}>
                                <Form.Item name="type" className="app-hidden">
                                  <Input type="hidden" />
                                </Form.Item>
                              </Col>

                              <Col xs={24} sm={12}>
                                <Form.Item
                                  name="reseller_number"
                                  className="app-hidden"
                                >
                                  <Input type="hidden" />
                                </Form.Item>
                              </Col>
                            </Row>

                            <div
                              style={{
                                width: "100%",
                                justifyContent: "space-between",
                                display: "flex",
                                margin: "10px 0",
                                gap: "10px",
                                flexWrap: "wrap",
                              }}
                            >
                              <Button
                                type="default"
                                loading={loading}
                                onClick={() => setPricingVisible(true)}
                              >
                                {t("back")}
                              </Button>

                              <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                              >
                                {t("sign_up")}
                              </Button>
                            </div>
                          </Form>
                        </div>
                      </Col>
                      <Col xs={24} sm={24} md={24} lg={2} xl={3} xxl={4} />
                    </Row>
                  </div>

                  <div style={{ display: OTPFormVisible ? "block" : "none" }}>
                    <Row>
                      <Col xs={24} sm={3} md={4} lg={6} xl={7} xxl={8} />
                      <Col xs={24} sm={18} md={16} lg={12} xl={10} xxl={8}>
                        <div
                          style={{
                            backgroundColor: "var(--color-white)",
                            padding: "30px",
                            boxShadow: "0 4px 20px var(--color-shadow-soft)",
                            borderRadius: "16px",
                            border: "1px solid var(--color-border)",
                          }}
                        >
                          <Form
                            name="validate-otp-form"
                            layout="vertical"
                            onFinish={onOTPFormFinish}
                            form={otpForm}
                          >
                            <div className="app-auth-form-footer">
                              <h2>{t("verify_otp")}</h2>
                              <p
                                style={{
                                  marginTop: "12px",
                                  color: "var(--color-heading)",
                                  fontSize: "17px",
                                }}
                              >
                                {t("otp_verify_subtitle")}
                              </p>
                            </div>

                            <p>
                              {t("opt_message")}
                              <br />
                              {t("if_you_want_to_change_phone_number")}{" "}
                              <Link
                                to="#"
                                onClick={() => setOTPFormVisible(false)}
                              >
                                {t("click_here")}
                              </Link>
                            </p>

                            <Form.Item
                              name="otp"
                              label={t("otp")}
                              rules={[
                                { required: true },
                                { max: 4 },
                                { min: 4 },
                              ]}
                              hasFeedback
                            >
                              <Input.Password
                                type="number"
                                minLength={4}
                                maxLength={4}
                                size="large"
                              />
                            </Form.Item>

                            <div
                              style={{
                                width: "100%",
                                margin: "20px 0 10px 0",
                                display: "flex",
                                justifyContent: "space-between",
                                gap: "10px",
                                flexWrap: "wrap",
                              }}
                            >
                              <Form.Item style={{ marginBottom: 0 }}>
                                <Button
                                  type="default"
                                  disabled={remainingTime >= 1}
                                  onClick={handleSendOTPSMSAgain}
                                >
                                  {t("resend_code")}
                                </Button>
                                <br />
                                <small
                                  style={{
                                    display:
                                      remainingTime < 1
                                        ? "none"
                                        : "inline-block",
                                  }}
                                >
                                  {t("enable_in")}{" "}
                                  {Math.floor(remainingTime / 60)
                                    .toString()
                                    .padStart(2, "0") +
                                    ":" +
                                    Math.floor(remainingTime % 60)
                                      .toString()
                                      .padStart(2, "0")}
                                </small>
                              </Form.Item>

                              <Form.Item style={{ marginBottom: 0 }}>
                                <Button
                                  type="primary"
                                  htmlType="submit"
                                  loading={loading}
                                >
                                  {t("validate")}
                                </Button>
                              </Form.Item>
                            </div>
                          </Form>
                        </div>
                      </Col>
                      <Col xs={24} sm={3} md={4} lg={6} xl={7} xxl={8} />
                    </Row>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Spin>
    </>
  );
};

const Register = () => {
  const { pathname, search } = useLocation();
  const tenantBaseDomain = (process.env.REACT_APP_TENANT_BASE_DOMAIN || "")
    .trim()
    .replace(/^\.+|\.+$/g, "");
  // The pathname check is load-bearing: filterPagesByAddons falls back to a
  // "*" route rendering this component whenever authUser is falsy, which
  // happens on every boot while /is-login is still in flight. Without it a
  // reload on a tenant host hard-navigates to the portal registration page.
  const shouldRedirect =
    Boolean(tenantBaseDomain) && isTenantHost() && pathname === "/register";

  useEffect(() => {
    if (!shouldRedirect) return;

    window.location.replace(
      `https://portal.${tenantBaseDomain}/register${search}`,
    );
  }, [search, shouldRedirect, tenantBaseDomain]);

  return shouldRedirect ? null : <RegisterForm />;
};

export default Register;
